import { isSettled } from '@nicolaudie-group/ng-ui-kit/helpers/isStatus'

import pinia from '@/stores/pinia'
import useProfileStore, {
  profileStatusKey,
  brandsStatusKey,
  constantsStatusKey,
  profileKey,
} from '@/stores/profile.store'
import useUserStore, { userStatus } from '@/stores/user.store'
import useSettingsStore, { groupsStatusKey } from '@/stores/settings.store'
import useSupportStore from '@/stores/support.store'

/**
 * Getters that return wether a certain required set of data was loaded
 */
const loaded = {
  /**
   * True if the profile is considered loaded in the profile store
   */
  get profile() {
    const profileStore = useProfileStore(pinia)
    /*
    Since the profile is editable by the user, it's status may be "PENDING" again
    during edition requests.
    For that reason, we also consider it loaded if any data is present, to avoid
    flashing the loader whenever we edit the profile.
    */
    return isSettled(profileStore[profileStatusKey]) || !!profileStore[profileKey]
  },
  /**
   * True if the user is considered loaded in the user store
   */
  get user() {
    const userStore = useUserStore(pinia)
    return isSettled(userStore[userStatus])
  },
  /**
   * True if all the required constants have been loaded in their
   * relevant store
   */
  get constants() {
    const settingsStore = useSettingsStore(pinia)
    const profileStore = useProfileStore(pinia)
    return (
      isSettled(settingsStore[groupsStatusKey])
      && isSettled(profileStore[brandsStatusKey])
      && isSettled(profileStore[constantsStatusKey])
    )
  },
  /**
   * True if the translations have been loaded
   */
  get translation() {
    const userStore = useUserStore(pinia)
    return isSettled(userStore.translationStatus) && userStore.language
  },
  /**
   * True if the support session have been loaded
   */
  get supportSession() {
    const supportStore = useSupportStore(pinia)
    return isSettled(supportStore.sessionStatus)
  },
}

/**
 * Hook stored on the routes meta, and called by the `App` component,
 * to check wether all required resources for the "Editor" view
 * have finished loading
 * @returns {Boolean} Wether all required resources have finished loading or not
 */
export const editor = () => (
  loaded.constants
  && loaded.user
  && loaded.translation
  && loaded.profile
  && loaded.supportSession
)

/**
 * Hook stored on the routes meta, and called by the `App` component,
 * to check wether all required resources for the "Home" view
 * have finished loading
 * @returns {Boolean} Wether all required resources have finished loading or not
 */
export const home = () => (
  loaded.constants
  && loaded.user
  && loaded.translation
  && loaded.supportSession
)

/**
 * Hook stored on the routes meta, and called by the `App` component,
 * to check wether all required resources for the "Create" view
 * have finished loading
 * @returns {Boolean} Wether all required resources have finished loading or not
 */
export const create = () => (
  loaded.constants
  && loaded.user
  && loaded.translation
  && loaded.supportSession
)

/**
 * Hook stored on the routes meta, and called by the `App` component,
 * to check wether all required resources for the "RequestRepair" view
 * have finished loading
 * @returns {Boolean} Wether all required resources have finished loading or not
 */
export const repair = () => (
  loaded.constants
  && loaded.user
  && loaded.translation
  && loaded.profile
  && loaded.supportSession
)

/**
 * Hook stored on the routes meta, and called by the `App` component,
 * to check wether all required resources for the "SupportSession" view
 * have finished loading
 * @returns {Boolean} Wether all required resources have finished loading or not
 */
export const supportSession = () => (
  loaded.user
  && loaded.translation
  && loaded.supportSession
)

/**
 * Hook stored on the routes meta, and called by the `App` component,
 * to bypass checking if all required resources have finished loading
 * @returns {Boolean} Always `true`
 */
export const bypass = () => true
